import { useCallback, useEffect, useRef, useState } from 'react';
import {
  channelMetaFrom,
  metaCacheLookup,
  videoMetaFrom,
  type CachedChannelMeta,
  type CachedVideoMeta,
  type InstanceConfig,
} from '@visious/core';
import { fetchChannelDetails } from '../api/channels';
import { fetchVideoDetails } from '../api/videos';
import { systemClock } from '../platform/clock';
import { getPlatform } from '../platform/instance';
import { putChannelMeta, touchChannelMeta, useChannelMetaCache } from '../platform/channelMetaStore';
import { putVideoMeta, touchVideoMeta, useVideoMetaCache } from '../platform/videoMetaStore';

/**
 * **Naming the links inside prose** — the title behind a `youtube.com/watch?v=…` in a description, and
 * the channel behind a `/channel/UC…`.
 *
 * R14's three rules, exactly as the History page and the subscription strip apply them, because this
 * is the same problem with the same payload: a video record is **89–262 KB** and a channel record
 * ~49 KB, and a description that lists a season of episodes holds twenty of them.
 *
 * 1. **The link renders without its record.** The URL the author wrote is on screen from the first
 *    paint and stays a working link; the title replaces it when it arrives. Nothing here can leave a
 *    blank.
 * 2. **Only what is on screen is fetched.** An `IntersectionObserver` per link, for the reason the
 *    History page gives: the browser already computes this, and a watch page's description is clamped
 *    to a few lines until the reader presses Show more, so the links below the fold cost nothing.
 * 3. **A stale hit renders and refreshes**, never blanks. That is `metaCacheLookup`'s own split
 *    between `stale` and `misses` and needs nothing added here.
 *
 * **And one rule the list pages do not need: a ceiling.** A grid asks for forty rows it drew itself,
 * spread over a scroll; a description can put twenty video links in one paragraph that all become
 * visible in the same frame, which at a quarter of a megabyte each is a page load's worth of traffic
 * for a block of text. So requests leave through a queue that keeps {@link MAX_IN_FLIGHT} on the wire
 * and holds the rest — every link is still named, just not all at once.
 */

/** How many metadata requests this module keeps on the wire. The rest wait their turn. */
const MAX_IN_FLIGHT = 3;

type Fetcher = (id: string) => Promise<void>;

interface Pending {
  readonly id: string;
  readonly run: Fetcher;
}

const waiting: Pending[] = [];
const active = new Set<string>();
/**
 * Ids that were asked for and did not answer, so each is attempted **once per visit**.
 *
 * The History page found this in a browser rather than reasoning about it: without it a failed
 * lookup stays a visible cache miss, so every *other* success changes the cache, re-renders the
 * block and asks for the failed one again. The cost of the rule is a link that keeps its URL until
 * the next visit; the cost of leaving it out is a request loop nobody sees.
 */
const failed = new Set<string>();

function pump(): void {
  while (active.size < MAX_IN_FLIGHT) {
    const next = waiting.shift();
    if (next === undefined) return;
    active.add(next.id);
    void next.run(next.id).finally(() => {
      active.delete(next.id);
      pump();
    });
  }
}

function request(id: string, run: Fetcher): void {
  if (id === '' || active.has(id) || failed.has(id)) return;
  if (waiting.some((entry) => entry.id === id)) return;
  waiting.push({ id, run });
  pump();
}

const fetchVideo: Fetcher = async (videoId) => {
  const boot = await getPlatform();
  if (!boot.ok) return;
  const result = await fetchVideoDetails(boot.platform, videoId);
  if (!result.ok) {
    // Every failure is the same failure here, unlike on the History page: a link is not a row of a
    // list the reader owns, so "this video is gone" and "the request did not answer" both mean the
    // link keeps the address the author wrote. There is nothing extra to say about either.
    failed.add(videoId);
    return;
  }
  putVideoMeta([videoMetaFrom(result.value, Math.floor(boot.platform.clock.nowEpochMs() / 1000))]);
};

const fetchChannel: Fetcher = async (channelId) => {
  const boot = await getPlatform();
  if (!boot.ok) return;
  const result = await fetchChannelDetails(boot.platform, channelId);
  if (!result.ok) {
    failed.add(channelId);
    return;
  }
  putChannelMeta([
    channelMetaFrom(
      boot.platform.config,
      result.value,
      Math.floor(boot.platform.clock.nowEpochMs() / 1000),
    ),
  ]);
};

/**
 * The configured instance, for the one thing a link needs it for: resolving a video frame's path into
 * a URL the queue can put on a card.
 *
 * Its own state rather than a shared hook, following the four screens that do the same — §3's
 * resolution is asynchronous and can fail, and `null` is a state this renders through rather than
 * waits on.
 */
export function useInstanceConfig(): InstanceConfig | null {
  const [config, setConfig] = useState<InstanceConfig | null>(null);
  useEffect(() => {
    let live = true;
    void getPlatform().then((boot) => {
      if (live && boot.ok) setConfig(boot.platform.config);
    });
    return () => {
      live = false;
    };
  }, []);
  return config;
}

export interface InlineMeta<T> {
  /** The record, or `null` while the link is still showing the address the author wrote. */
  readonly meta: T | null;
  /**
   * Attach to the element the link draws. Nothing is fetched before it reports itself on screen —
   * a callback ref, so it runs in the commit phase (`useHistory`'s `useVisibleRows` says why).
   */
  readonly observe: (element: Element | null) => void;
}

/** The title, channel and duration behind a linked video. */
export function useInlineVideo(videoId: string): InlineMeta<CachedVideoMeta> {
  const cache = useVideoMetaCache();
  const nowEpochSec = Math.floor(systemClock.nowEpochMs() / 1000);
  const found = metaCacheLookup(cache, [videoId], nowEpochSec);
  const meta = found.hits[0] ?? null;
  // A hit that is not stale is the whole answer, and asking anyway would spend a quarter of a
  // megabyte to be told the title again. A stale one renders *and* refreshes — R14's third rule.
  const observe = useOnScreen(videoId, fetchVideo, meta === null || found.stale.length > 0);

  // R14's LRU half: rendering from the cache is a use, and a cache that only records fetches sorts
  // by insertion order wearing an LRU field's name.
  useEffect(() => {
    if (meta !== null) touchVideoMeta([videoId], Math.floor(systemClock.nowEpochMs() / 1000));
    // Keyed on whether there is a record rather than on the record, which is a new object per read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, meta !== null]);

  return { meta, observe };
}

/** The name behind a channel linked by its id. */
export function useInlineChannel(channelId: string): InlineMeta<CachedChannelMeta> {
  const cache = useChannelMetaCache();
  const nowEpochSec = Math.floor(systemClock.nowEpochMs() / 1000);
  const found = metaCacheLookup(cache, [channelId], nowEpochSec);
  const entry = found.hits[0] ?? null;
  const observe = useOnScreen(channelId, fetchChannel, entry === null || found.stale.length > 0);
  // A tombstone carries an empty name (`channelGoneMeta`), which is a record with nothing to show —
  // the link keeps its address rather than rendering as a blank.
  const meta = entry === null || entry.name === '' ? null : entry;

  useEffect(() => {
    if (meta !== null) touchChannelMeta([channelId], Math.floor(systemClock.nowEpochMs() / 1000));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId, meta !== null]);

  return { meta, observe };
}

/**
 * One `IntersectionObserver` for the whole app rather than one per link: a description with twenty
 * links would otherwise build twenty observers, and the browser is already watching the same
 * viewport for all of them.
 *
 * A link that has scrolled away is left registered. Un-registering it would achieve nothing — the
 * record is cached — and re-asking on the way back is the opposite of the point.
 */
function useOnScreen(
  id: string,
  run: Fetcher,
  /** Whether there is anything to fetch — read at the moment the link appears, not at mount. */
  wanted: boolean,
): (element: Element | null) => void {
  const asked = useRef(false);
  // A ref rather than a dependency: the callback ref must not change identity as the cache fills, or
  // React would detach and re-attach the element on every render and the observer would restart.
  // Written in an effect rather than during the render — a ref is not render state, and the observer
  // reads it asynchronously afterwards, so the commit has always run by then.
  const want = useRef(wanted);
  useEffect(() => {
    want.current = wanted;
  }, [wanted]);
  return useCallback(
    (element: Element | null) => {
      if (element === null || asked.current) return;
      asked.current = true;
      watch(element, () => {
        if (want.current) request(id, run);
      });
    },
    [id, run],
  );
}

const onScreen = new Map<Element, () => void>();
let observer: IntersectionObserver | null = null;

function watch(element: Element, ask: () => void): void {
  if (typeof IntersectionObserver === 'undefined') {
    // No observer (an old browser, a test environment): ask on mount rather than never. Degrading to
    // eager is a worse page; degrading to "no titles at all" is a broken one.
    ask();
    return;
  }
  observer ??= new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const pending = onScreen.get(entry.target);
        if (pending === undefined) continue;
        onScreen.delete(entry.target);
        observer?.unobserve(entry.target);
        pending();
      }
    },
    // Tight, like the comment list's: prose is read top to bottom, and a generous margin would name
    // the whole of a description nobody has scrolled to.
    { rootMargin: '120px' },
  );
  onScreen.set(element, ask);
  observer.observe(element);
}
